import ErrorComponent from "@/components/atoms/ErrorComponent";
import IconButton from "@/components/atoms/IconButton";
import Loader from "@/components/atoms/Loader";
import Modal from "@/components/atoms/Modal";
import { SelectPaginateAsync } from "@/components/atoms/Select";
import AreYouSurToContinueModal from "@/components/molecules/modals/AreYouSurToContinueModal";
import { IModalMethods } from "@/interfaces/Modal";
import useLoadOptions from "@/hooks/useLoadOptions";
import { CalendarVisibility } from "@/interfaces/CalendarVisibility";
import User from "@/interfaces/IUser";
import {
  useDestroyCalendarVisibilityMutation,
  useGetCalendarVisibilitiesQuery,
  useStoreCalendarVisibilityMutation,
} from "@/services/calendarVisibilities";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { ElementRef, FC, useRef, useState } from "react";
import { Button, Card, Col, Form, Row, Table } from "react-bootstrap";
import { ToastContentProps, toast } from "react-toastify";

const CalendarVisibilitiesView: FC = () => {
  const { loadUsersOptions } = useLoadOptions();
  const StoreModalRef = useRef<IModalMethods>(null);
  const DeleteModalRef =
    useRef<ElementRef<typeof AreYouSurToContinueModal>>(null);

  const [pageIndex, setPageIndex] = useState(0);
  const pageSize = 50;

  const [viewer, setViewer] = useState<User | null>(null);
  const [target, setTarget] = useState<User | null>(null);

  const { data, isLoading, isError, error } = useGetCalendarVisibilitiesQuery({
    pageIndex,
    pageSize,
  });
  const [storeAsync, { isLoading: isStoring }] =
    useStoreCalendarVisibilityMutation();
  const [destroyAsync] = useDestroyCalendarVisibilityMutation();

  const onOpenStore = () => {
    setViewer(null);
    setTarget(null);
    StoreModalRef.current?.showOrHiddenModal();
  };

  const onSubmit = () => {
    if (!viewer || !target) {
      toast.error("Sélectionnez un observateur et une cible");
      return;
    }
    if (viewer.id === target.id) {
      toast.error("Observateur et cible doivent être différents");
      return;
    }
    toast.promise(
      storeAsync({
        viewerUserId: viewer.id,
        targetUserId: target.id,
      }).unwrap(),
      {
        pending: "Création du mapping en cours ...",
        success: {
          render() {
            StoreModalRef.current?.showOrHiddenModal();
            return (
              <p className="mb-0 text-center">Mapping créé</p>
            );
          },
        },
        error: {
          render({ data }: ToastContentProps<FetchBaseQueryError>) {
            return "Une erreur est survenue";
          },
        },
      },
    );
  };

  const onDelete = async (id: string) => {
    await toast.promise(destroyAsync(id).unwrap(), {
      pending: "Suppression en cours ...",
      success: {
        render() {
          DeleteModalRef.current?.showOrHiddenModal();
          return <p className="mb-0 text-center">Mapping supprimé</p>;
        },
      },
      error: {
        render({ data }: ToastContentProps<FetchBaseQueryError>) {
          return "Une erreur est survenue";
        },
      },
    });
  };

  if (isLoading) return <Loader absolute />;
  if (isError) return <ErrorComponent error={error} absolute returnButton />;

  const rows = data?.data ?? [];

  return (
    <div className="formalys-page formalys-list-page">
      <Card>
      <AreYouSurToContinueModal
        ref={DeleteModalRef}
        onContinue={onDelete}
        title="Souhaitez-vous vraiment supprimer ce mapping ?"
      />
      <Modal
        ref={StoreModalRef}
        title="Nouveau mapping de visibilité"
        onSubmit={onSubmit}
        isLoading={isStoring}
        continueTitle="Créer"
        requiredLabel
      >
        <Row>
          <Col lg={6}>
            <Form.Group className="mb-3">
              <Form.Label>
                <span className="text-danger">*</span> Observateur (viewer)
              </Form.Label>
              <SelectPaginateAsync
                loadOptions={loadUsersOptions}
                getOptionLabel={(o) => `${o.firstname} ${o.lastname}`}
                getOptionValue={(o) => o.id.toString()}
                value={viewer}
                onChange={(v) => setViewer(v as User | null)}
                isClearable
                placeholder="Sélectionner..."
              />
            </Form.Group>
          </Col>
          <Col lg={6}>
            <Form.Group className="mb-3">
              <Form.Label>
                <span className="text-danger">*</span> Cible (target)
              </Form.Label>
              <SelectPaginateAsync
                loadOptions={loadUsersOptions}
                getOptionLabel={(o) => `${o.firstname} ${o.lastname}`}
                getOptionValue={(o) => o.id.toString()}
                value={target}
                onChange={(v) => setTarget(v as User | null)}
                isClearable
                placeholder="Sélectionner..."
              />
            </Form.Group>
          </Col>
        </Row>
      </Modal>

      <Card.Header className="d-flex justify-content-between align-items-center">
        <h5 className="mb-0">Visibilité calendrier</h5>
        <IconButton
          variant="nolina-default"
          size="sm"
          icon={faPlus}
          onClick={onOpenStore}
          transform="shrink-3"
          iconAlign="middle"
        >
          <span className="ms-1">Nouveau</span>
        </IconButton>
      </Card.Header>
      <Card.Body>
        <Table responsive hover>
          <thead>
            <tr>
              <th>Observateur</th>
              <th>Cible</th>
              <th style={{ width: 100 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row: CalendarVisibility) => (
              <tr key={row.id}>
                <td>
                  {row.viewer
                    ? `${row.viewer.firstname} ${row.viewer.lastname}`
                    : row.viewerUserId}
                </td>
                <td>
                  {row.target
                    ? `${row.target.firstname} ${row.target.lastname}`
                    : row.targetUserId}
                </td>
                <td>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() =>
                      DeleteModalRef.current?.showOrHiddenModal(row.id)
                    }
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </Button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={3} className="text-center text-muted py-3">
                  Aucun mapping configuré
                </td>
              </tr>
            )}
          </tbody>
        </Table>
        {data?.meta && data.meta.lastPage > 1 && (
          <div className="d-flex justify-content-between align-items-center mt-2">
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={pageIndex === 0}
              onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
            >
              Précédent
            </Button>
            <span className="fs--1">
              Page {data.meta.currentPage} / {data.meta.lastPage}
            </span>
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={pageIndex + 1 >= data.meta.lastPage}
              onClick={() => setPageIndex((p) => p + 1)}
            >
              Suivant
            </Button>
          </div>
        )}
      </Card.Body>
      </Card>
    </div>
  );
};

export default CalendarVisibilitiesView;
